/**
 * Physics and game constants for jgolf.
 *
 * These values are reverse-engineered from the original Jippii / Aapeli /
 * Playforia minigolf client (agolf.game.HackedShot / GameCanvas) and verified
 * against community ports. They are kept in the original "native" coordinate
 * space (a 735x375 playfield made of 15px tiles) so the authentic feel is
 * preserved verbatim. The client renders this space scaled up for crispness.
 */

// ─────────────────────────────────────────────────────────────
// Playfield geometry (native units = original pixels)
// ─────────────────────────────────────────────────────────────
export const TILE_SIZE = 15;
export const MAP_COLS = 49;
export const MAP_ROWS = 25;
export const FIELD_WIDTH = MAP_COLS * TILE_SIZE; // 735
export const FIELD_HEIGHT = MAP_ROWS * TILE_SIZE; // 375

export const BALL_RADIUS = 6.5; // 13px diameter
export const HOLE_RADIUS = 6.5;

/** Inner clamp for the ball center against the outer field walls. */
export const FIELD_MIN_X = 6.6;
export const FIELD_MAX_X = FIELD_WIDTH - 7.1; // 727.9
export const FIELD_MIN_Y = 6.6;
export const FIELD_MAX_Y = FIELD_HEIGHT - 7.1; // 367.9

// ─────────────────────────────────────────────────────────────
// Stroke power (authentic)
//   scale = clamp((dragDistance - DEAD_ZONE) / POWER_DIVISOR, MIN_POWER, MAX_POWER)
//   velocity = unit(drag) * scale
// ─────────────────────────────────────────────────────────────
export const POWER_DEAD_ZONE = 5.0;
export const POWER_DIVISOR = 30.0;
export const MIN_POWER = 0.075;
export const MAX_POWER = 6.5;
/** The original aim line maxes out at 200px when power reaches MAX_POWER. */
export const AIM_LINE_MAX_PX = 200;
/** Tiny seeded per-shot perturbation (per axis), as in the original Shot. */
export const STROKE_NOISE = 0.05;

// ─────────────────────────────────────────────────────────────
// Simulation integration (authentic)
//   Each frame advances 10 substeps of (pos += speed * SUBSTEP_DT),
//   checking collision every substep (continuous collision detection).
// ─────────────────────────────────────────────────────────────
export const SUBSTEPS = 10;
export const SUBSTEP_DT = 0.1;
export const MAX_SPEED = 7.0; // hard speed cap after friction
export const STOP_SPEED = 0.075; // below this (and not on a force tile) the ball stops

/** Diagonal between substeps when sampling collisions: round(6 / sqrt(2)). */
export const COLLISION_CARDINAL = 6;
export const COLLISION_DIAGONAL = 4; // (int)(6 * (1/sqrt2) + 0.5)

// Settle / hole timers (in frames; one frame == one physics iteration)
export const HOLE_SETTLE_FRAMES = 22; // ~2.17s of original "time" at 0.1/frame
export const WATER_SETTLE_FRAMES = 60; // ~6.0s
export const HOLE_SINK_NEIGHBORS = 6; // forgiving capture when most neighbour samples are hole
export const HOLE_CAPTURE_MAX_SPEED = 3.5; // faster balls can skim across the cup

// Stroke timeout safety (in frames). The authentic friction is gentle, so a
// full-power roll on open grass can take ~750 frames to settle; these give it
// room to stop naturally before the safety net kicks in. After SOFT, bounciness
// decays to 0; after HARD, the stroke is force-stopped so a turn can't hang.
export const STROKE_SOFT_TIMEOUT_FRAMES = 950;
export const STROKE_HARD_TIMEOUT_FRAMES = 1200;
export const MAX_SIM_FRAMES = 1400; // absolute ceiling

// Force-tile accelerations (per frame)
export const DOWNHILL_ACCEL = 0.025; // speed arrows
export const HOLE_SUCTION_ACCEL = 0.055; // pull toward cup
export const DIAG = Math.SQRT1_2; // 1/sqrt(2)

// Magnets: linear-falloff radial force applied within MAGNET_RADIUS of the
// magnet center (attract pulls in, repel pushes out).
export const MAGNET_RADIUS = 75; // 5 tiles
export const MAGNET_ACCEL = 0.045; // per frame at the magnet center
/** Frames of continuous magnet influence before a ball may come to rest. */
export const MAGNET_INFLUENCE_CAP = 300;

/** Min frames between two hits degrading/moving the same tile (debounce). */
export const MUTATION_COOLDOWN_FRAMES = 10;

// Ball-ball collision
export const BALL_COLLISION_DAMPING = 0.75; // both balls * this after a hit

// ─────────────────────────────────────────────────────────────
// Replay sampling (network)
//   The server records the ball positions every SAMPLE_INTERVAL frames and
//   sends them so clients can replay the stroke smoothly.
// ─────────────────────────────────────────────────────────────
// The original iterated physics at a fixed 6ms cadence (~166 Hz);
// 2 frames/sample at 6ms keeps bandwidth modest and matches that pacing.
export const SAMPLE_INTERVAL = 2; // frames per replay sample
export const FRAME_MS = 6; // ms per frame for replay timing (authentic 166 Hz)

// ─────────────────────────────────────────────────────────────
// Match / room defaults
// ─────────────────────────────────────────────────────────────
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 4;
export const MAX_SPECTATORS = 16;
export const ROOM_CODE_LENGTH = 3;

export const DEFAULT_HOLES_PER_ROUND = 9;
export const DEFAULT_MAX_STROKES = 10; // per-hole cap (0 = unlimited)
export const DEFAULT_TURN_TIMEOUT_MS = 30_000; // 0 = no timer
export const SHOT_COOLDOWN_MS = 250; // anti-spam

/** Water reset behavior. */
export const WATER_RESET_TEE = 0; // back to tee
export const WATER_RESET_SHORE = 1; // back to last dry position
export const WATER_PENALTY_STROKES = 1;
